import { Bot, Sparkles, Stars } from "lucide-react";
import { avatarOptionFor } from "@/lib/profileStore";

/**
 * Phase 9 — gradient circle avatar.
 *
 * Sizes: sm (h-8 w-8), md (h-12 w-12), lg (h-24 w-24), xl (h-28 w-28).
 *
 * For non-kid avatars: shows the first letter of the profile name in
 * tracking-tighter sans serif. For kid_* avatars: shows a lucide icon
 * (Bot/Sparkles/Stars) instead — keeps the playful vibe without using
 * actual emoji characters.
 */
const SIZE_CLASS = {
  xs: "h-7 w-7 text-[11px]",
  sm: "h-8 w-8 text-[12px]",
  md: "h-12 w-12 text-base",
  lg: "h-24 w-24 text-3xl",
  xl: "h-28 w-28 text-4xl",
};

const ICON_SIZE = {
  xs: "w-3 h-3",
  sm: "w-3.5 h-3.5",
  md: "w-5 h-5",
  lg: "w-10 h-10",
  xl: "w-12 h-12",
};

export default function ProfileAvatar({
  profile,
  avatarKey,
  name,
  size = "md",
  ring = true,
  className = "",
  active = false,
}) {
  const key = profile?.avatar_key || avatarKey || "violet";
  const opt = avatarOptionFor(key);
  const isKid = !!opt?.isKidVariant;
  const initial = (profile?.name || name || "?").trim().charAt(0).toUpperCase();
  const iconClass = ICON_SIZE[size] || ICON_SIZE.md;

  const renderIcon = () => {
    if (!isKid) return null;
    if (opt.icon === "panda") return <Sparkles className={iconClass} strokeWidth={2.5} />;
    if (opt.icon === "robot") return <Bot className={iconClass} strokeWidth={2.5} />;
    if (opt.icon === "unicorn") return <Stars className={iconClass} strokeWidth={2.5} />;
    return null;
  };

  return (
    <div
      className={`${SIZE_CLASS[size] || SIZE_CLASS.md} grid place-items-center rounded-md bg-gradient-to-br ${opt.gradient} text-white font-display font-black tracking-tighter shadow-lg ${ring ? "ring-1 ring-white/15" : ""} ${active ? "outline outline-2 outline-offset-4 outline-white" : ""} transition-all ${className}`}
      data-testid="profile-avatar"
      data-avatar-key={key}
    >
      {isKid ? renderIcon() : <span aria-hidden="true">{initial}</span>}
    </div>
  );
}
